import { cn } from "@au-schein-de/au-ui-library";
import { UBER_DIRECT_MAX_BASKET_CENTS } from "@dransay/api/models";
import type { TFunction } from "i18next";
import { type KeyboardEvent, useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { ExpressCourierInfoDialog } from "~/components/express-courier-info-dialog.component";
import { ExpressCourierMark } from "~/components/express-courier-mark.component";
import { formatCentsAsAmount } from "~/hooks/use-currency-price.hook";
import {
	EXPRESS_COURIER_OPERATING_HOURS,
	expressCourierProviderName,
} from "~/tooling/express-courier-operating-hours";

interface ExpressCourierOperatingHours {
	startTime: string;
	endTime: string;
}

type ExpressCourierDisabledReason =
	| "location"
	| "time"
	| "day"
	| "basket"
	| "unavailable";

interface ExpressCourierCardProps {
	isSelected: boolean;
	isPending: boolean;
	onSelect: () => void;
	priceLabel?: string;
	provider?: string | null;
	/** Shown on the disabled-time line. Uber passes the pharmacy window. */
	operatingHours?: ExpressCourierOperatingHours;
	/**
	 * Reasoned unavailability from `useExpressCourierAvailability` (FR-059).
	 * When set, the card renders greyed and non-interactive with a reason
	 * line instead of the badge/description/"Learn how it works" link.
	 * "unavailable" covers a settled-but-indeterminate check (FR-062).
	 */
	disabledReason?: ExpressCourierDisabledReason;
}

const CARD_CLASS_NAME =
	"w-full rounded-xl border-2 p-[16px] text-left transition-colors";

function expressCourierToneClass(
	isDisabled: boolean,
	isSelected: boolean,
): string {
	if (isDisabled) {
		return "text-gray-500";
	}
	if (isSelected) {
		return "text-ansay-700";
	}
	return "text-gray-900";
}

function expressCourierCardClassName(
	isDisabled: boolean,
	isSelected: boolean,
	isPending: boolean,
): string {
	if (isDisabled) {
		return cn(CARD_CLASS_NAME, "cursor-default border-transparent bg-gray-200");
	}
	return cn(
		CARD_CLASS_NAME,
		"cursor-pointer",
		isSelected
			? "border-ansay-main bg-white"
			: "border-transparent bg-gray-100 hover:bg-gray-200",
		isPending && !isSelected && "pointer-events-none",
	);
}

function expressCourierDescription(
	t: TFunction<"checkout">,
	disabledReason: ExpressCourierDisabledReason | undefined,
	providerName: string | null,
	operatingHours: ExpressCourierOperatingHours | undefined,
): string {
	const provider = providerName ?? "";
	switch (disabledReason) {
		case "location":
			return t("expressCourier.disabledLocation");
		case "time":
			return t("expressCourier.disabledTime", {
				provider,
				...(operatingHours ?? EXPRESS_COURIER_OPERATING_HOURS),
			});
		case "day":
			return t("expressCourier.disabledDay", { provider });
		case "basket":
			return t("expressCourier.disabledBasket", {
				amount: formatCentsAsAmount(UBER_DIRECT_MAX_BASKET_CENTS),
			});
		case "unavailable":
			return t("expressCourier.error.unavailable");
		default:
			return providerName
				? t("expressCourier.available", { provider: providerName })
				: t("expressCourier.description");
	}
}

function expressCourierAriaLabel(
	providerName: string | null,
	title: string,
	priceLabel: string | undefined,
): string {
	const providerPrefix = providerName ? `${providerName} ` : "";
	return `${providerPrefix}${title}, ${priceLabel ?? ""}`;
}

export function ExpressCourierCard({
	isSelected,
	isPending,
	onSelect,
	priceLabel,
	provider,
	operatingHours,
	disabledReason,
}: ExpressCourierCardProps) {
	const { t } = useTranslation("checkout");
	const isDisabled = disabledReason !== undefined;
	const providerName = expressCourierProviderName(provider);
	const description = expressCourierDescription(
		t,
		disabledReason,
		providerName,
		operatingHours,
	);
	const reasonId = useId();
	const describedById = isDisabled ? reasonId : undefined;
	const toneClass = expressCourierToneClass(isDisabled, isSelected);
	const handleCardSelect = useCallback(() => {
		if (isDisabled || isPending || isSelected) {
			return;
		}
		onSelect();
	}, [isDisabled, isPending, isSelected, onSelect]);
	const handleCardKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== "Enter" && event.key !== " ") {
				return;
			}
			event.preventDefault();
			handleCardSelect();
		},
		[handleCardSelect],
	);

	return (
		<div
			// biome-ignore lint/a11y/useSemanticElements: custom card layout, same pattern as DeliveryMethodCard
			role="radio"
			aria-checked={isSelected}
			aria-disabled={isDisabled}
			tabIndex={isDisabled ? -1 : 0}
			aria-label={expressCourierAriaLabel(
				providerName,
				t("expressCourier.title"),
				priceLabel,
			)}
			aria-describedby={describedById}
			onClick={handleCardSelect}
			onKeyDown={handleCardKeyDown}
			className={expressCourierCardClassName(isDisabled, isSelected, isPending)}
		>
			<div className="flex items-center gap-2 md:gap-3">
				<span
					className={cn(
						"shrink-0 [&_svg]:size-6 [&_svg]:text-current",
						toneClass,
					)}
				>
					<ExpressCourierMark provider={provider} />
				</span>
				<span className={cn("text-body-bold", toneClass)}>
					{t("expressCourier.title")}
				</span>
				{!isDisabled && (
					<span className="relative flex items-start gap-3 overflow-hidden rounded-full bg-[linear-gradient(45deg,transparent_25%,color-mix(in_srgb,var(--color-ansay-300)_28%,transparent)_50%,transparent_75%,transparent_100%)] bg-ansay-700 bg-no-repeat px-[9px] py-1 text-caption-emphasis uppercase text-ansay-light [background-position:-100%_0,0_0] [background-size:250%_250%,100%_100%] [animation:express-courier-badge-shine_1.8s_linear_infinite]">
						{t("expressCourier.badge")}
					</span>
				)}
				<span
					className={cn(
						"ml-auto flex shrink-0 items-center justify-end gap-2 text-body-bold",
						toneClass,
					)}
				>
					{priceLabel}
				</span>
			</div>
			<p
				id={describedById}
				className="mt-[9px] max-w-[48ch] text-body-sm tracking-[-.02em] text-gray-500"
			>
				{description}
			</p>
			{!isDisabled && (
				<ExpressCourierInfoDialog
					provider={provider}
					trigger={
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation();
							}}
							className="flex h-8 cursor-pointer items-center gap-2 py-3 text-body-sm-emphasis text-ansay-700"
						>
							{t("expressCourier.learnHowItWorks")}
						</button>
					}
				/>
			)}
		</div>
	);
}
